import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { GUIDEABLE_LAYERS, MODULE_GUIDES, getGuidePath } from '../../constants/moduleGuides';
import { buildSiteUrl, SITE_NAME } from '../../constants/site';
import { getLayerFromSlug, getLayerName, getLayerSlug } from '../../utils/routeUtils';
import type { ServiceLayer } from '../../types/gis';
import { districtMap } from '../../i18n/districts';
import { translations } from '../../i18n/translations';
import { useStripDefaultMeta } from '../../hooks/useStripDefaultMeta';
import { useMapStore } from '../../store/useMapStore';
import './guides.css';

const isGuideableLayer = (layer: ServiceLayer): boolean => GUIDEABLE_LAYERS.includes(layer);

const OG_IMAGE = buildSiteUrl('/branding/og-image.webp');

const ModuleGuidePage: React.FC = () => {
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  const guideLayer = parts[0] === 'guides' ? parts[1] : undefined;
  const resolvedLayer = guideLayer ? getLayerFromSlug(guideLayer) : null;
  const language = useMapStore(state => state.language);
  const isTamil = language === 'ta';

  // Both branches below render their own complete og/twitter set, so strip the
  // static index.html defaults to prevent duplicate, generic social tags.
  useStripDefaultMeta();

  if (resolvedLayer && isGuideableLayer(resolvedLayer)) {
    const content = MODULE_GUIDES[resolvedLayer];
    const layerSlug = getLayerSlug(resolvedLayer);
    const canonical = buildSiteUrl(getGuidePath(resolvedLayer));
    const exploreUrl = `/${layerSlug}`;
    const districtExampleUrl = `/${layerSlug}/Chennai`;

    const handleCtaClick = (buttonName: string) => {
      if (typeof window !== 'undefined' && 'gtag' in window) {
        (window as any).gtag('event', 'click', {
          event_category: 'Guide_CTA',
          event_label: buttonName,
          value: 1
        });
      }
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.faq.flatMap(item => {
        const list = [
          {
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer
            }
          }
        ];
        if (item.question_ta && item.answer_ta) {
          list.push({
            '@type': 'Question',
            name: item.question_ta,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer_ta
            }
          });
        }
        return list;
      })
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: SITE_NAME, item: buildSiteUrl('/') },
        { '@type': 'ListItem', position: 2, name: 'Guides', item: buildSiteUrl('/guides') },
        { '@type': 'ListItem', position: 3, name: getLayerName(resolvedLayer), item: canonical }
      ]
    };

    return (
      <div className="guide-shell">
        <Helmet>
          <title>{`${content.title} | ${SITE_NAME}`}</title>
          <meta name="description" content={content.summary} />
          <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
          <link rel="canonical" href={canonical} />
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content={SITE_NAME} />
          <meta property="og:title" content={`${content.title} | ${SITE_NAME}`} />
          <meta property="og:description" content={content.summary} />
          <meta property="og:url" content={canonical} />
          <meta property="og:image" content={OG_IMAGE} />
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:site" content="@NammaMap" />
          <meta property="twitter:title" content={`${content.title} | ${SITE_NAME}`} />
          <meta property="twitter:description" content={content.summary} />
          <meta property="twitter:image" content={OG_IMAGE} />
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>

        <header className="guide-hero glass">
          <h1>{content.title}</h1>
          <p>{content.summary}</p>
          <div className="guide-cta-row">
            <Link 
              className="guide-cta guide-cta-web" 
              to={exploreUrl}
              onClick={() => handleCtaClick(`${getLayerName(resolvedLayer)} Web Map CTA`)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <span>{content.ctaText || `Search ${getLayerName(resolvedLayer)} Map`}</span>
            </Link>

            <a 
              className="guide-cta guide-cta-app" 
              href="https://play.google.com/store/apps/details?id=in.nammamap.app"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCtaClick(`${getLayerName(resolvedLayer)} Play Store App CTA`)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px' }}><path d="M3.609 1.814L13.793 12 3.61 22.186a2.38 2.38 0 0 1-.61-.914c-.16-.436-.16-.94 0-1.408V4.136c-.16-.468-.16-.972 0-1.408.15-.36.36-.67.61-.914zm11.24 11.242l2.302-2.302-11.89-6.864 9.588 9.166zm0 1.888L5.26 24.11l11.89-6.864-2.302-2.302zm1.464-1.464l3.195-1.845a1.293 1.293 0 0 0 0-2.242l-3.195-1.845-2.01 2.01 2.01 1.922z"/></svg>
              <span className="app-btn-text-mobile">Get Android App (Google Play)</span>
              <span className="app-btn-text-desktop">Android App on Google Play</span>
            </a>

            <Link 
              className="guide-cta guide-cta-secondary" 
              to={districtExampleUrl}
              onClick={() => handleCtaClick(`${getLayerName(resolvedLayer)} Secondary CTA`)}
            >
              District Example: Chennai
            </Link>
          </div>
        </header>

        {content.alertTitle && content.alertText && (
          <section className="guide-section glass" style={{ backgroundColor: 'rgba(255, 244, 229, 0.4)', borderLeft: '4px solid #f59e0b' }}>
            <h2 style={{ color: '#b45309', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <span style={{ fontSize: '1.25rem' }}>🔍</span> {content.alertTitle}
            </h2>
            <p style={{ marginTop: '0.5rem', marginBottom: '0', fontWeight: 500 }} dangerouslySetInnerHTML={{ __html: content.alertText.replace(content.ctaText || 'Search', `<strong>${content.ctaText || 'Search'}</strong>`) }} />
          </section>
        )}

        <section className="guide-section glass guide-section-intro">
          <h2>Comprehensive Overview</h2>
          <p style={{ lineHeight: 1.7, opacity: 0.9, fontSize: '1.05rem' }}>
            {content.inDepthOverview}
          </p>
        </section>

        <section className="guide-section glass">
          <h2>Why This Module Matters</h2>
          <ul>
            {content.whyItMatters.map(point => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>

        <section className="guide-section glass">
          <h2>How To Use</h2>
          <ol>
            {content.howToUse.map(step => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>

        <section className="guide-section glass">
          <h2>Common Use Cases</h2>
          <ul>
            {content.useCases.map(useCase => (
              <li key={useCase}>{useCase}</li>
            ))}
          </ul>
        </section>

        <section className="guide-section glass">
          <h2>{isTamil ? 'அடிக்கடி கேட்கப்படும் கேள்விகள் (FAQ)' : 'Frequently Asked Questions (FAQ)'}</h2>
          {content.faq.map(item => {
            return (
              <article key={item.question} className="guide-faq-item">
                <h3 className="guide-faq-question">
                  <span>{item.question}</span>
                  {item.question_ta && (
                    <span className="guide-faq-question-ta"> / {item.question_ta}</span>
                  )}
                </h3>
                
                <p className="guide-faq-answer-en">
                  {item.answer}
                </p>

                {item.answer_ta && (
                  <p className="guide-faq-answer-ta">
                    {item.answer_ta}
                  </p>
                )}

                {item.actions && item.actions.length > 0 && (
                  <div className="guide-faq-actions" style={{ marginTop: '0.75rem', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {item.actions.map(action => {
                      const isInternal = action.url.startsWith('/') || action.url.includes('nammamap.in');
                      const internalPath = action.url.replace('https://nammamap.in', '') || '/';
                      const linkStyle: React.CSSProperties = {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.85rem',
                        color: isInternal ? '#059669' : '#2563eb',
                        textDecoration: 'none',
                        background: isInternal ? 'rgba(5, 150, 105, 0.08)' : 'rgba(37, 99, 235, 0.08)',
                        border: isInternal ? '1px solid rgba(5, 150, 105, 0.25)' : '1px solid rgba(37, 99, 235, 0.2)',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontWeight: 600
                      };

                      if (isInternal) {
                        return (
                          <Link key={action.url} to={internalPath} style={linkStyle}>
                            <span>{action.label_en}</span>
                            {action.label_ta && (
                              <span style={{ opacity: 0.85, fontSize: '0.8rem', fontWeight: 500 }}>({action.label_ta})</span>
                            )}
                            <span>→</span>
                          </Link>
                        );
                      }

                      return (
                        <a
                          key={action.url}
                          href={action.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={linkStyle}
                        >
                          <span>{action.label_en}</span>
                          {action.label_ta && (
                            <span style={{ opacity: 0.85, fontSize: '0.8rem', fontWeight: 500 }}>({action.label_ta})</span>
                          )}
                          <span>→</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })}
        </section>
        
        <section className="guide-section glass">
          <h2>District-wise Quick Links</h2>
          <p style={{ marginBottom: '1rem', opacity: 0.8 }}>Access {getLayerName(resolvedLayer)} directly for your district:</p>
          <div className="guide-district-grid">
            {Object.keys(districtMap).sort().map(district => {
              const tamilDistrict = districtMap[district] || district;
              const tamilLayerName = (translations.ta as any)[resolvedLayer] || getLayerName(resolvedLayer);
              return (
                <Link 
                  key={district} 
                  to={`/${layerSlug}/${district}`}
                  className="guide-district-link"
                  title={`${getLayerName(resolvedLayer)} in ${district}`}
                >
                  <span className="district-link-en">{getLayerName(resolvedLayer)} in {district}</span>
                  <span className="district-link-ta"> ({tamilDistrict} {tamilLayerName})</span>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="guide-section glass">
          <h2>Explore Other Guides</h2>
          <ul>
            {GUIDEABLE_LAYERS.filter(layer => layer !== resolvedLayer).map(layer => (
              <li key={layer}>
                <Link to={getGuidePath(layer)}>{getLayerName(layer)}</Link>
              </li>
            ))}
          </ul>
        </section>
      </div>
    );
  }

  const indexTitle = `Tamil Nadu Civic Map Guides | ${SITE_NAME}`;
  const indexDescription = 'Module guides for pincode, ration shops, police, health, TNEB, local bodies, and constituency maps in Tamil Nadu.';
  const indexUrl = buildSiteUrl('/guides');

  return (
    <div className="guide-shell">
      <Helmet>
        <title>{indexTitle}</title>
        <meta name="description" content={indexDescription} />
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
        <link rel="canonical" href={indexUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={indexTitle} />
        <meta property="og:description" content={indexDescription} />
        <meta property="og:url" content={indexUrl} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:site" content="@NammaMap" />
        <meta property="twitter:title" content={indexTitle} />
        <meta property="twitter:description" content={indexDescription} />
        <meta property="twitter:image" content={OG_IMAGE} />
      </Helmet>

      <section className="guide-hero glass">
        <h1>Tamil Nadu Civic Map Guides</h1>
        <p>Learn how to use each module effectively and access deep-linkable map views.</p>
      </section>

      <section className="guide-grid">
        {GUIDEABLE_LAYERS.map(layer => (
          <Link key={layer} to={getGuidePath(layer)} className="guide-card glass">
            <h2>{getLayerName(layer)}</h2>
            <p>{MODULE_GUIDES[layer].summary}</p>
          </Link>
        ))}
      </section>
    </div>
  );
};

export default ModuleGuidePage;
